<template>
  <div
    class="splash-screen"
    :class="{ 'is-leaving': isLeaving }"
    @click="startClose"
  >
    <!-- 격자 배경 (평소엔 검정 배경 역할, 퇴장 시 타일이 하나씩 사라짐) -->
    <div class="grid-bg">
      <div
        v-for="(cell, i) in gridCells"
        :key="i"
        class="grid-cell"
        :style="{
          left: cell.x + '%',
          top: cell.y + '%',
          width: cell.w + '%',
          height: cell.h + '%',
          transitionDelay: gridAnimating ? cell.delay + 'ms' : '0ms'
        }"
        :class="{ 'is-gone': gridAnimating }"
      ></div>
    </div>

    <canvas ref="canvas" class="particle-canvas" :class="{ 'canvas-exit': isLeaving }"></canvas>

    <!-- 중앙 텍스트 -->
    <div class="splash-center" :class="{ 'text-exit': isLeaving, 'phase-copy': copyStarted, 'phase-eng': engVisible }">
      <!-- 영문: 위로 밀려 올라가면서 등장 -->
      <p class="eng-line" :class="{ visible: engVisible }">The Summit of Investment</p>

      <!-- 메인 텍스트 한 줄: 브랜드 + 카피 -->
      <div class="main-line">
        <span class="brand-text" :class="{ visible: brandVisible }">테크노 레이원시티</span>
        <span class="comma-space" :class="{ visible: copyStarted }">,&nbsp;</span>
        <span class="copy-wrap" :class="{ visible: copyStarted }">
          <span
            v-for="(char, i) in copyChars"
            :key="i"
            class="copy-char"
            :class="{ visible: copyRevealed[i], 'is-space': char === ' ' }"
          >{{ char === ' ' ? '\u00A0' : char }}</span>
        </span>
      </div>

      <!-- 로고: 영문과 함께 등장 -->
      <div class="splash-logo" :class="{ visible: engVisible }">
        <img src="@/assets/logo/logo_text.png" alt="레이원시티" />
      </div>
    </div>

    <!-- 프로그레스 -->
    <div class="progress" :class="{ 'fade-out': isLeaving }">
      <div class="progress-track">
        <div class="progress-fill"></div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SplashScreen',
  data() {
    return {
      isLeaving: false,
      gridAnimating: false,
      gridCells: [],
      dots: [],
      animFrame: null,
      startTime: 0,
      brandVisible: false,
      copyText: '투자의 정점에 서다',
      copyRevealed: [],
      copyStarted: false,
      engVisible: false,
      exitStarted: false
    }
  },
  computed: {
    copyChars() {
      return this.copyText.split('')
    }
  },
  mounted() {
    document.body.style.overflow = 'hidden'
    this.copyRevealed = new Array(this.copyText.length).fill(false)
    this.startTime = performance.now()
    this.generateGrid()
    this.initCanvas()
    this.scheduleAnimations()
    this.autoCloseTimer = setTimeout(() => this.startClose(), 6500)
  },
  beforeUnmount() {
    if (this.autoCloseTimer) clearTimeout(this.autoCloseTimer)
    if (this.closeTimer) clearTimeout(this.closeTimer)
    if (this.animFrame) cancelAnimationFrame(this.animFrame)
    if (this.textTimers) this.textTimers.forEach(t => clearTimeout(t))
    window.removeEventListener('resize', this.resizeCanvas)
    document.body.style.overflow = ''
  },
  methods: {
    generateGrid() {
      const cols = 12
      const rows = 8
      const cellW = 100 / cols
      const cellH = 100 / rows
      const cells = []
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          // 랜덤 + 약간의 중앙→바깥 가중치
          const cx = (c + 0.5) / cols
          const cy = (r + 0.5) / rows
          const dist = Math.sqrt(Math.pow(cx - 0.5, 2) + Math.pow(cy - 0.5, 2))
          const maxDist = Math.sqrt(0.25 + 0.25)
          const delay = Math.random() * 400 + (dist / maxDist) * 400
          cells.push({
            x: c * cellW,
            y: r * cellH,
            w: cellW + 0.1,  // 살짝 겹침으로 틈 방지
            h: cellH + 0.1,
            delay: Math.round(delay)
          })
        }
      }
      this.gridCells = cells
    },

    initCanvas() {
      const canvas = this.$refs.canvas
      if (!canvas) return
      this.ctx = canvas.getContext('2d')
      this.resizeCanvas()
      window.addEventListener('resize', this.resizeCanvas)
      this.createParticles()
      this.animate()
    },
    resizeCanvas() {
      const canvas = this.$refs.canvas
      if (!canvas) return
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      canvas.width = window.innerWidth * dpr
      canvas.height = window.innerHeight * dpr
      canvas.style.width = window.innerWidth + 'px'
      canvas.style.height = window.innerHeight + 'px'
      if (this.ctx) this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    },

    createParticles() {
      const w = window.innerWidth
      const h = window.innerHeight
      this.dots = []

      // 1) 배경 먼지 — 화면 전체에 빽빽하게
      for (let i = 0; i < 300; i++) {
        this.dots.push({
          x: Math.random() * w,
          y: Math.random() * h,
          r: 0.5 + Math.random() * 1.2,
          vx: (Math.random() - 0.5) * 0.05,
          vy: -0.008 - Math.random() * 0.04,
          opacity: 0.08 + Math.random() * 0.2,
          phase: Math.random() * Math.PI * 2,
          pulseSpeed: 0.0003 + Math.random() * 0.0005,
          fadeIn: 0,
          fadeInSpeed: 0.003 + Math.random() * 0.005,
          exitFade: false,
          exitOpacity: 0
        })
      }

      // 2) 중간 파티클 — 잘 보이는 크기
      for (let i = 0; i < 120; i++) {
        this.dots.push({
          x: Math.random() * w,
          y: Math.random() * h,
          r: 1.2 + Math.random() * 2,
          vx: (Math.random() - 0.5) * 0.08,
          vy: -0.015 - Math.random() * 0.06,
          opacity: 0.15 + Math.random() * 0.25,
          phase: Math.random() * Math.PI * 2,
          pulseSpeed: 0.0003 + Math.random() * 0.0007,
          fadeIn: 0,
          fadeInSpeed: 0.004 + Math.random() * 0.006,
          exitFade: false,
          exitOpacity: 0
        })
      }

      // 3) 글로우 포인트 — 눈에 띄는 밝은 점
      for (let i = 0; i < 40; i++) {
        this.dots.push({
          x: Math.random() * w,
          y: Math.random() * h,
          r: 2 + Math.random() * 3,
          vx: (Math.random() - 0.5) * 0.04,
          vy: -0.01 - Math.random() * 0.03,
          opacity: 0.12 + Math.random() * 0.2,
          phase: Math.random() * Math.PI * 2,
          pulseSpeed: 0.0002 + Math.random() * 0.0004,
          fadeIn: 0,
          fadeInSpeed: 0.002 + Math.random() * 0.004,
          exitFade: false,
          exitOpacity: 0
        })
      }
    },

    animate() {
      const canvas = this.$refs.canvas
      if (!canvas) return
      const ctx = this.ctx
      const w = window.innerWidth
      const h = window.innerHeight
      const elapsed = (performance.now() - this.startTime) / 1000

      ctx.clearRect(0, 0, w, h)

      if (elapsed > 0.3) {
        // 중앙 은은한 글로우
        this.drawCenterGlow(ctx, w, h, elapsed)
        // 미세 파티클
        ctx.globalCompositeOperation = 'lighter'
        this.drawDots(ctx, w, h)
        ctx.globalCompositeOperation = 'source-over'
      }

      this.animFrame = requestAnimationFrame(this.animate)
    },

    drawCenterGlow(ctx, w, h, elapsed) {
      // 중앙에 아주 은은한 원형 글로우 — 서서히 등장
      const glowFade = Math.min(1, elapsed / 3)
      const radius = Math.min(w, h) * 0.45
      const grad = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, radius)
      const alpha = 0.06 * glowFade
      grad.addColorStop(0, `rgba(27, 42, 74, ${alpha})`)
      grad.addColorStop(0.4, `rgba(27, 42, 74, ${alpha * 0.4})`)
      grad.addColorStop(1, 'rgba(27, 42, 74, 0)')
      ctx.fillStyle = grad
      ctx.fillRect(0, 0, w, h)
    },

    drawDots(ctx, w, h) {
      const now = performance.now()
      this.dots.forEach(d => {
        if (d.fadeIn < 1) d.fadeIn = Math.min(1, d.fadeIn + d.fadeInSpeed)
        d.x += d.vx
        d.y += d.vy
        if (d.exitFade) {
          d.exitOpacity -= 0.015
          if (d.exitOpacity < 0) d.exitOpacity = 0
        } else {
          if (d.y + d.r < 0) { d.y = h + d.r; d.x = Math.random() * w }
          if (d.x < -d.r) d.x = w + d.r
          if (d.x > w + d.r) d.x = -d.r
        }
        const breathe = Math.sin(now * d.pulseSpeed + d.phase) * 0.3 + 0.7
        const baseAlpha = d.exitFade ? d.exitOpacity : d.opacity
        const alpha = baseAlpha * breathe * d.fadeIn
        if (alpha <= 0.001) return

        // 미세 먼지 — 작은 흰색 점만
        ctx.beginPath()
        ctx.arc(d.x, d.y, d.r * breathe, 0, Math.PI * 2)
        ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`
        ctx.fill()
      })
    },

    scheduleAnimations() {
      this.textTimers = []

      // 0.8s: "테크노 레이원시티" 중앙에 등장
      const t1 = setTimeout(() => { this.brandVisible = true }, 800)
      this.textTimers.push(t1)

      // 2.5s: 카피 영역 천천히 열리기 시작
      const t2 = setTimeout(() => { this.copyStarted = true }, 2500)
      this.textTimers.push(t2)

      // 3.0s~: "투자의 정점에 서다" 글자 하나씩 올라옴 (카피 영역 열리는 중에 자연스럽게)
      this.copyChars.forEach((_, i) => {
        const t = setTimeout(() => {
          this.copyRevealed[i] = true
        }, 3000 + i * 120)
        this.textTimers.push(t)
      })

      // 4.2s: 영문 위에서 내려옴
      const t3 = setTimeout(() => { this.engVisible = true }, 4200)
      this.textTimers.push(t3)
    },

    startClose() {
      if (this.isLeaving) return
      if (this.autoCloseTimer) clearTimeout(this.autoCloseTimer)
      this.isLeaving = true
      this.exitStarted = true

      this.dots.forEach(d => {
        d.exitFade = true
        d.exitOpacity = d.opacity * d.fadeIn
      })

      // 텍스트+캔버스 먼저 사라지고(0.4s), 그 후 격자 타일 분해 시작
      setTimeout(() => {
        this.gridAnimating = true
      }, 400)

      // 격자 완전히 사라진 후 emit
      this.closeTimer = setTimeout(() => {
        if (this.animFrame) cancelAnimationFrame(this.animFrame)
        document.body.style.overflow = ''
        this.$emit('close')
      }, 1800)
    }
  }
}
</script>

<style scoped>
.splash-screen {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  overflow: hidden;
}

/* ===== 격자 배경 ===== */
.grid-bg {
  position: absolute;
  inset: 0;
  z-index: 0;
}

.grid-cell {
  position: absolute;
  background: #0a1628;
  transition: opacity 0.6s ease;
}

.grid-cell.is-gone {
  opacity: 0;
}

/* ===== 캔버스 ===== */
.particle-canvas {
  position: absolute;
  inset: 0;
  z-index: 1;
  transition: opacity 0.4s ease;
}

.particle-canvas.canvas-exit {
  opacity: 0;
}

/* ===== 중앙 텍스트 ===== */
.splash-center {
  position: relative;
  z-index: 10;
  text-align: center;
  transition: transform 1.8s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.4s ease;
}

/* 3단계: 영문 등장 — 살짝 아래로 밀림 */
.splash-center.phase-eng {
  transform: translateY(20px);
}

.splash-center.text-exit {
  opacity: 0;
  transform: translateY(20px) scale(0.97);
}

/* 영문 — 필기체 크게, absolute로 main-line 위에 배치 */
.eng-line {
  position: absolute;
  left: 50%;
  bottom: 100%;
  transform: translateX(-50%);
  white-space: nowrap;
  font-family: 'Great Vibes', cursive;
  font-style: normal;
  font-weight: 400;
  font-size: 82px;
  color: rgba(255, 255, 255, 0.25);
  letter-spacing: 3px;
  margin-bottom: 24px;
  opacity: 0;
  transition: opacity 1.4s ease;
}

.eng-line.visible {
  opacity: 1;
}

/* 메인 한 줄 — justify-content: center로 항상 가운데 정렬 */
.main-line {
  position: relative;
  display: flex;
  align-items: baseline;
  justify-content: center;
  white-space: nowrap;
  transition: all 8s cubic-bezier(0.05, 0.5, 0.1, 1);
}

/* 브랜드 텍스트 */
.brand-text {
  font-family: var(--font-pretendard);
  font-size: 46px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.7);
  letter-spacing: 3px;
  opacity: 0;
  transition: opacity 1s ease;
}

.brand-text.visible {
  opacity: 1;
}

/* 쉼표+공백 — max-width로 스무스하게 열림 */
.comma-space {
  font-family: var(--font-pretendard);
  font-size: 46px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.7);
  display: inline-block;
  max-width: 0;
  opacity: 0;
  overflow: hidden;
  transition: max-width 8s cubic-bezier(0.02, 0.5, 0.1, 1), opacity 3s ease 0.5s;
}

.comma-space.visible {
  max-width: 30px;
  opacity: 1;
}

/* 카피 래퍼 — max-width로 스무스하게 열림 */
.copy-wrap {
  display: inline-flex;
  align-items: baseline;
  max-width: 0;
  overflow: hidden;
  transition: max-width 8s cubic-bezier(0.02, 0.5, 0.1, 1);
}

.copy-wrap.visible {
  max-width: 600px;
}

/* 카피 글자 하나씩 */
.copy-char {
  font-family: var(--font-pretendard);
  font-size: 46px;
  font-weight: 700;
  color: #fff;
  letter-spacing: 2px;
  display: inline-block;
  opacity: 0;
  transform: translateY(16px);
  transition: opacity 0.6s ease, transform 0.6s cubic-bezier(0.16, 1, 0.3, 1);
}

.copy-char.visible {
  opacity: 1;
  transform: translateY(0);
}

.copy-char.is-space {
  width: 0.3em;
}

/* ===== 로고 ===== */
.splash-logo {
  margin-top: 36px;
  opacity: 0;
  transform: translateY(12px);
  transition: opacity 1.2s ease, transform 1.2s cubic-bezier(0.16, 1, 0.3, 1);
}

.splash-logo.visible {
  opacity: 1;
  transform: translateY(0);
}

.splash-logo img {
  height: 44px;
  width: auto;
  filter: brightness(0) invert(1);
  opacity: 0.4;
}

/* ===== 프로그레스 ===== */
.progress {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10;
  transition: opacity 0.3s;
}

.progress.fade-out { opacity: 0; }

.progress-track {
  width: 100%;
  height: 1px;
  background: rgba(255, 255, 255, 0.02);
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #8B6914, #D4A54A, #FCEABB, #D4A54A);
  background-size: 200% 100%;
  animation: progressGo 6.5s linear forwards, shimmer 3s ease-in-out infinite;
}

@keyframes progressGo {
  from { width: 0; }
  to { width: 100%; }
}

@keyframes shimmer {
  0%, 100% { background-position: 0% 0; }
  50% { background-position: 100% 0; }
}

/* ===== 반응형 ===== */
@media (max-width: 900px) {
  .brand-text, .copy-char, .comma-space { font-size: 32px; }
  .eng-line { font-size: 56px; }
  .eng-line.visible { height: 70px; }
  .splash-logo img { height: 36px; }
}

@media (max-width: 768px) {
  .splash-screen {
    padding: 20px;
  }

  .splash-center {
    width: 100%;
    max-width: 100vw;
    padding: 0 12px;
    box-sizing: border-box;
  }

  .brand-text, .copy-char, .comma-space {
    font-size: 26px;
  }

  .eng-line {
    font-size: 44px;
    letter-spacing: 1px;
  }

  .eng-line.visible {
    height: 56px;
  }

  .main-line {
    flex-wrap: wrap;
    justify-content: center;
    white-space: normal;
  }

  .splash-logo {
    margin-top: 28px;
  }

  .splash-logo img {
    height: 32px;
  }

  .splash-center.phase-eng {
    transform: translateY(16px);
  }

  .progress-track {
    height: 2px;
  }
}

@media (max-width: 600px) {
  .brand-text, .copy-char, .comma-space { font-size: 22px; }
  .eng-line { font-size: 36px; letter-spacing: 0.5px; }
  .eng-line.visible { height: 46px; }
  .splash-center.phase-eng { transform: translateY(12px); }
  .splash-logo img { height: 26px; }
  .splash-logo { margin-top: 20px; }
}

@media (max-width: 380px) {
  .brand-text, .copy-char, .comma-space { font-size: 18px; }
  .eng-line { font-size: 28px; }
  .eng-line.visible { height: 38px; }
  .splash-logo img { height: 22px; }
}
</style>
